import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Search, X } from "lucide-react";
import type { Principal } from "@cd/core";
import { api, ApiError, isGotabOperator, type SignInVia, type StaffLocation } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, Input } from "../components/ui/primitives";
import { cn } from "../lib/utils";

const MAX_LOCATIONS = 50;

/** Which sign-in lets this person choose their locations, or null if theirs are fixed. */
export const pickerVia = (me: Principal): SignInVia | null =>
  isGotabOperator(me) ? "gotab" : me.userId.startsWith("staff:") ? "staff" : null;

/**
 * Before an MCP client's grant (the only time locations are chosen before entering the app): pick
 * the locations the session is scoped to.
 */
export function SelectLocations() {
  const via: SignInVia = new URLSearchParams(window.location.search).get("via") === "gotab" ? "gotab" : "staff";
  return (
    <div className="flex h-full items-center justify-center p-6">
      <Card className="w-full max-w-lg p-8">
        <LocationPicker
          via={via}
          // Back to where sign-in started (an MCP client's consent page), else home.
          onDone={(next) => window.location.assign(next?.startsWith("/") ? next : "/")}
        />
      </Card>
    </div>
  );
}

/**
 * Choose the locations this session is scoped to: any location for staff ("view as"), or one of
 * their own for a GoTab operator with too many to include at once.
 */
export function LocationPicker({ via, initial = [], onDone }: { via: SignInVia; initial?: StaffLocation[]; onDone: (next?: string) => void }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [picked, setPicked] = useState<StaffLocation[]>(initial);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const results = useQuery({
    queryKey: ["picker-locations", via, debounced],
    queryFn: () => api.pickerLocations(via, debounced),
    enabled: debounced.length >= 2,
    retry: false,
  });
  const start = useMutation({
    mutationFn: () => api.startPickedSession(via, picked.map((l) => l.id)),
    onSuccess: (r) => onDone(r.next),
  });

  const isPicked = (id: string) => picked.some((l) => l.id === id);
  const toggle = (l: StaffLocation) =>
    setPicked((cur) => (isPicked(l.id) ? cur.filter((x) => x.id !== l.id) : cur.length < MAX_LOCATIONS ? [...cur, l] : cur));

  // cf_sso or gotab.io session missing or expired: send them back through sign-in.
  const signedOut = results.error instanceof ApiError && results.error.status === 401;

  return (
    <>
    <h1 className="text-xl font-semibold tracking-tight">Choose locations</h1>
    <p className="mt-2 text-sm text-muted">
      Dashboards will show data for these locations only. You can change them any time from your account menu.
    </p>

    {signedOut ? (
      <p className="mt-6 text-sm">
        Your GoTab sign-in has expired. <a className="text-accent underline" href={`/api/auth/${via}`}>Sign in again</a>
      </p>
    ) : (
      <>
        <div className="relative mt-6">
          <Search className="pointer-events-none absolute left-2.5 top-2 size-4 text-subtle" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Location name or ID" className="pl-8" />
        </div>

        <div className="mt-2 max-h-72 overflow-y-auto">
          {results.data?.map((l) => (
            <button
              key={l.id}
              onClick={() => toggle(l)}
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-surface-2"
            >
              <span className={cn("flex size-4 items-center justify-center rounded border border-border-strong", isPicked(l.id) && "border-accent bg-accent text-white")}>
                {isPicked(l.id) && <Check className="size-3" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{l.name}</span>
                <span className="block truncate text-xs text-subtle">{[l.place, `#${l.id}`].filter(Boolean).join(" · ")}</span>
              </span>
            </button>
          ))}
          {results.isSuccess && !results.data.length && <p className="px-2 py-2 text-sm text-subtle">No matching locations.</p>}
          {results.error && !signedOut && <p className="px-2 py-2 text-sm text-bad">{results.error.message}</p>}
        </div>

        {picked.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {picked.map((l) => (
              <span key={l.id} className="inline-flex items-center gap-1 rounded-md bg-accent-soft px-2 py-1 text-xs text-accent">
                {l.name}
                <button onClick={() => toggle(l)} aria-label={`Remove ${l.name}`}><X className="size-3" /></button>
              </span>
            ))}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <span className="text-xs text-subtle">{picked.length}/{MAX_LOCATIONS} selected</span>
          <Button variant="primary" disabled={!picked.length || start.isPending} onClick={() => start.mutate()}>
            Continue
          </Button>
        </div>
        {start.error && <p className="mt-2 text-sm text-bad">{start.error.message}</p>}
      </>
    )}
    </>
  );
}
